import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Tooltip,
	Typography,
	useMediaQuery,
} from '@mui/material';
import axios from 'axios';
import { useState } from 'react';
import {
	BooleanField,
	BooleanInput,
	BulkActionProps,
	CreateButton,
	DatagridConfigurable,
	ExportButton,
	FilterButton,
	FunctionField,
	List,
	NumberField,
	SelectColumnsButton,
	SimpleList,
	TextInput,
	useNotify,
	useResourceContext,
} from 'react-admin';
import constants from '../../constants';
import { downloadEncodedCSV } from '../../utils/functions';
import MyPagination from '../MyPagination';
import moment from 'moment';

const CopyEmailsButton = (props: BulkActionProps) => {
	const notify = useNotify();
	const handleClick = async () => {
		try {
			const response = await axios.get(`${constants.API_URL}/admin/company-users-emails`, {
				params: { filter: { ids: props.selectedIds } },
			});
			navigator.clipboard.writeText(response.data);
			notify('הועתק', { type: 'success' });
		} catch (error) {
			notify('רגע... נסו שוב', { type: 'error' });
		}
	};
	return <Button onClick={handleClick}>COPY EMAIL TO CLIPBOARD</Button>;
};
const CompanyList = () => {
	const [showBlackBackgrounds, setShowBlackBackgrounds] = useState(false);
	const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
	const context = useResourceContext({ resource: 'company' });
	const [isDownloadingFile, setIsDownloadingFile] = useState(false);
	const notify = useNotify();
	const renderCompanyLogo = (record: any) => (
		<span
			style={{
				flex: 1,
				display: 'flex',
				height: 65,
				width: 65,
				alignItems: 'center',
				backgroundColor: showBlackBackgrounds ? 'black' : undefined,
				justifyContent: 'center',
			}}
		>
			<span
				style={{
					display: 'flex',
					textAlign: 'center',
					justifyContent: 'center',
					alignItems: 'center',
					borderRadius: 60,
					height: 60,
					width: 60,
					backgroundColor: record.logo_background || 'white',
				}}
			>
				<img
					src={record.logo}
					style={{ display: 'flex', height: 50, width: 50, objectFit: 'contain' }}
					alt={`${record.english_name}_logo`}
				/>
			</span>
		</span>
	);
	const exporter = (company: any) => {
		const companyForExport = company.map((company: any) => ({
			name: company.english_name,
			subscription_start: company.subscription_start,
			active: company.active,
		}));

		downloadEncodedCSV(companyForExport, ['name', 'subscription_start', 'active'], 'company');
	};

	const downloadExecutiveSummaryData = async () => {
		try {
			setIsDownloadingFile(true);
			const response = await axios.get(`${constants.API_URL}/company/summary-files`, {
				responseType: 'blob', // Get raw file data
			});
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `data_${moment().format('DD_MM_YY')}.json`); // Set file name
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			notify('הקובץ נוצר בהצלחה', { type: 'success' });
		} catch (error) {
			notify('שגיאה ביצירת הקובץ', { type: 'error' });
		} finally {
			setIsDownloadingFile(false);
		}
	};

	const exportCompanyAvatars = async (companyId: string, companyName: string) => {
		console.log(companyId, companyName);
		
		const url = `${constants.API_URL}member/company-data-main-screen/${companyId}`;
		let finalData: any = null;
		let buffer = '';
		
		try {
			const response = await axios({
				method: 'GET',
				url: url,
				responseType: 'text',
				onDownloadProgress: (progressEvent: any) => {
					const xhr = progressEvent.target;
					const responseText = xhr.response || xhr.responseText;
					
					// Process only new data since last update
					const newData = responseText.substring(buffer.length);
					buffer = responseText;
					
					const lines = newData.split('\n');
					
					for (const line of lines) {
						if (line.startsWith('data: ')) {
							try {
								const data = JSON.parse(line.substring(6));
								
								switch(data.type) {
									case 'start':
										console.log(`Starting: ${data.total} members`);
										notify(`מתחיל לעבד ${data.total} משתמשים...`, { type: 'info' });
										break;
										
									case 'progress':
										console.log(`${data.percentage}% (${data.processed}/${data.total})`);
										notify(`מעבד... ${data.percentage}%`, { type: 'info' });
										break;
										
									case 'complete':
										notify('העיבוד הושלם! מוריד קובץ...', { type: 'success' });
										break;
										
									case 'data':
										console.log('Received data event:', data);
										finalData = data.data;
										console.log('finalData set to:', finalData?.length || 0, 'items');
										break;
								}
							} catch (e) {
								// Skip invalid JSON lines
							}
						}
					}
				}
			});
			
			// Parse the complete response to get the data event
			const allLines = response.data.split('\n');
			for (const line of allLines) {
				if (line.startsWith('data: ')) {
					try {
						const data = JSON.parse(line.substring(6));
						if (data.type === 'data') {
							finalData = data.data;
							break;
						}
					} catch (e) {
						// Skip invalid JSON lines
					}
				}
			}
			
			console.log('After axios complete, finalData:', finalData?.length || 0, 'items');
			
			if (finalData) {
				// Download the JSON file
				const jsonBlob = new Blob([JSON.stringify(finalData, null, 2)], { 
					type: 'application/json' 
				});
				const downloadUrl = window.URL.createObjectURL(jsonBlob);
				const link = document.createElement('a');
				link.href = downloadUrl;
				link.setAttribute('download', `${companyName}_avatars_${moment().format('DD_MM_YY')}.json`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(downloadUrl);
				
				notify('הקובץ נוצר בהצלחה', { type: 'success' });
			}
		} catch (error) {
			console.error('Error:', error);
			notify('שגיאה ביצירת הקובץ', { type: 'error' });
		}
	};
	return (
		<List
			filters={[
				<TextInput source="english_name" label="שם" alwaysOn />,
				<BooleanInput source="active" label="חברות פעילות" dir="rtl" />,
			]}
			filterDefaultValues={{ active: true }}
			perPage={100}
			sort={{ field: 'english_name', order: 'ASC' }}
			pagination={<MyPagination />}
			actions={
				<div
					style={{
						display: 'flex',
						flex: 1,
						alignItems: 'center',
						justifyContent: 'flex-end',
						paddingInlineEnd: 16,
						gap: 12,
					}}
				>
					<ExportButton dir="ltr" exporter={exporter} />
					<FilterButton dir="ltr" style={{ minWidth: 130 }} />
					<CreateButton dir="ltr" />
					<SelectColumnsButton dir="ltr" style={{ minWidth: 100 }} />
					<Button disabled={isDownloadingFile} onClick={downloadExecutiveSummaryData}>
						<span style={{ minWidth: 100, lineHeight: '0.8rem' }}>
							{isDownloadingFile ? (
								<CircularProgress size={12} />
							) : (
								<span>
									ייצוא נתונים
									<br />
									<span style={{ fontSize: '0.7rem' }}>תקציר מנהלים</span>
								</span>
							)}
						</span>
					</Button>
					<FormControlLabel
						label={<Typography variant="body1">הדגמת רקע שחור ללוגו (כמו באפליקציה)</Typography>}
						control={
							<Checkbox
								onChange={(e) => setShowBlackBackgrounds(e.target.checked)}
								checked={showBlackBackgrounds}
							/>
						}
					/>
				</div>
			}
		>
			{isSmall ? (
				<SimpleList
					resource={context}
					sx={{ textAlign: 'right' }}
					style={{ textAlign: 'right' }}
					primaryText={(record) => (
						<Typography textAlign="right" fontWeight={'600'}>
							{`${record.english_name}`}
						</Typography>
					)}
					secondaryText={(record) => (
						<img
							width="70px"
							height="30px"
							style={{ objectFit: 'contain' }}
							src={`${record.logo}`}
							alt="logo"
						/>
					)}
				/>
			) : (
				<DatagridConfigurable
					rowClick="edit"
					size="small"
					dir="rtl"
					bulkActionButtons={<CopyEmailsButton />}
					sx={{
						'& .column-undefined': { width: 12 },
						'& .column-allow_proactive_app': { maxWidth: 20 },
						'& .column-boostsCount': { maxWidth: 10 },
						'& .column-active': { maxWidth: 10 },
					}}
				>
					<FunctionField textAlign="center" render={renderCompanyLogo} margin={0} label="לוגו" />
					<FunctionField
						textAlign="right"
						fontWeight={'bold'}
						sortBy="english_name"
						render={(record: any) => (
							<div>
								{record.english_name}
								<span className="ms-2">
									{record.domains?.length && record.allDomainsVerified ? (
										<Tooltip title="כל הדומיינים מאושרים">
											<span className="text-green-500">🟢</span>
										</Tooltip>
									) : (
										<Tooltip
											title={
												!record.domains?.length
													? 'לא קיימים דומיינים'
													: !record.allDomainsVerified
													? 'לא כל הדומיינים מאומתים'
													: undefined
											}
										>
											<span className="text-red-500">🔴</span>
										</Tooltip>
									)}
								</span>
							</div>
						)}
						label="שם חברה"
					/>
					<FunctionField
						label="רשומים"
						render={(record: any) => (
							<span className="block min-w-20">
								<Typography fontWeight={'600'}>
									{`פעילים: ${record.employees?.real ?? 0}`}
									{/* {record.employees?.factor ? (
										<Typography display={'block'} variant="caption">{`פקטור: ${
											record.employees?.factor ?? 0
										}`}</Typography>
									) : null} */}
								</Typography>
							</span>
						)}
					/>
					<BooleanField textAlign="right" label="תכנית פרואקטיבית" source="allow_proactive_app" looseValue />
					<NumberField
						source="boostsCount"
						label={
							<div className="">
								<span>בוסטים</span>
								<br className="leading-none" />
								<p className="text-xs leading-tight text-gray-500">ב-3 חודשים האחרונים</p>
							</div>
						}
					/>
					<NumberField source="nps" label="NPS" />
					<FunctionField
						label="האם מסובסד?"
						textAlign="center"
						render={(record: any) => (
							<span>{Array.isArray(record.safe_space_prices) && record.safe_space_prices.length > 0 ? 'v' : 'x'}</span>
						)}
					/>
					<FunctionField
						label="שיחות הכוונה?"
						textAlign="center"
						render={(record: any) => (
							<span>{record.remove_extra_call === true ? '1' : '2'}</span>
						)}
					/>
					<FunctionField
						label="HR"
						render={(record: any) => (
							<Tooltip
								hidden={!record.hrs.length}
								title={
									<ol className="list-decimal px-3 py-1">
										{record.hrs?.map((x: any) => (
											<li>
												{`${x.first_name} ${x.last_name}`}
												{ <div style={{ fontSize: '0.85em', color: '#fff' }}>{x.email}</div>}
												
											</li>
										))}
									</ol>
								}
							>
								<div className="text-center w-20">
									<span className="block">{record.hrs?.length}</span>
									<span className="block text-xs">{record.showHrSection ? 'מתחם  HR זמין' : ''}</span>
									{record.hrs?.length > 0 && (
										<div className="text-xs mt-1">
											{record.hrs?.map((x: any, index: number) => (
												<div key={index} style={{ fontSize: '0.7em', color: '#666' }}>
													{x.email || ''}
												</div>
											))}
										</div>
									)}
								</div>
							</Tooltip>
						)}
					/>ֿ
					<FunctionField
						label="אווטר של משתמשים"
						render={(record: any) => {
							
							return (
							<div className="text-center">
								<Button
									onClick={(e) => {
										e.stopPropagation();
										exportCompanyAvatars(record.id, record.english_name);
									}}
									size="small"
								>
									ייצוא אווטרים
								</Button>
							</div>
							);
						}}
					/>
					<FunctionField
						label="בני משפחה שהוזמנו"
						render={(record: any) => {
							
							return (
								<div className="text-center">
									<span className="block">{record.familyMemberCount}</span>
								</div>
							);
						}}
					/>
				<NumberField source="computed_usages" label="מספר שימושים" textAlign="center" />
						<BooleanField textAlign="right" label="הצג באנר פרטי" source="show_private_banner" />
					<BooleanField textAlign="right" label="פעיל?" source="active" />
				
					
				</DatagridConfigurable>
			)}
		</List>
	);
};
export default CompanyList;
